import React, { useState } from "react";
import { X, Trash2, Plus, AlertTriangle, Lock } from "lucide-react";
import API from "../../services/api";
import { errMsg, prettyDate } from "../runsheets/util";
import { DAY_TYPES, FLAG_TINT, fmtHours } from "./util";

/* One crew member, one day. Correcting a day and keying a missing one in are
 * the same job for the operator, so both live here: every cell in the grid
 * opens this, whether or not there is anything in it yet.
 *
 * Breaks are recorded as start and finish times, not as a bare number of
 * minutes (operator directive, 22 Sept 2026). They are PAID: paid hours stay
 * finish minus start and nothing here ever takes a break off the hours. The
 * rows are sent as a whole list (PUT /time/shifts/{id}/breaks) after the
 * shift's own fields, so a changed start or finish is on the server before
 * the breaks are checked against it. */

const INPUT = "bg-slate-950 border border-slate-700/80 rounded-lg px-3 py-2 text-slate-200 outline-none focus:border-indigo-500 w-full";
const TIME_INPUT = `${INPUT} [&::-webkit-calendar-picker-indicator]:invert`;
const LABEL = "block text-[10px] uppercase tracking-widest text-slate-500 font-bold";

// Break rows carry their own key so removing one in the middle of the list
// does not hand the inputs below it the wrong values.
let rowSeq = 0;
const breakRow = (b) => {
  rowSeq += 1;
  return { key: `b${rowSeq}`, start: b?.start || "", finish: b?.finish || "" };
};

// The rows exactly as they are PUT - "HH:MM" strings the server formatted,
// or that the office typed. An open break (no finish yet) reads "".
const breakBody = (rows) => rows.map((r) => ({ start: r.start || "", finish: r.finish || "" }));
const sameBreaks = (a, b) => JSON.stringify(breakBody(a)) === JSON.stringify(breakBody(b));

// `heldBreaks` are rows the office typed that the server refused on the last
// save (DayEditor keeps them by shift id); they take the place of the
// server's rows so the operator can fix them rather than type them again.
const draftOf = (shift, heldBreaks) => ({
  start: shift?.start || "",
  finish: shift?.finish || "",
  day_type: shift?.day_type || "normal",
  note: shift?.note || "",
  breaks: (heldBreaks || shift?.breaks || []).map(breakRow),
});

function ShiftForm({ shift, heldBreaks, busy, onSubmit, onDelete }) {
  const [draft, setDraft] = useState(() => draftOf(shift, heldBreaks));
  const set = (patch) => setDraft({ ...draft, ...patch });
  const isNew = !shift;

  const original = shift?.breaks || [];
  const breaksChanged = !sameBreaks(draft.breaks, original);
  // The server refuses a break without a finish, so an edited list has to be
  // whole before it goes. A list the office has not touched (say an open
  // break on a shift still running) is left alone - the PUT is skipped.
  const incomplete = breaksChanged && draft.breaks.some((b) => !b.start || !b.finish);
  // A day keyed in before breaks had times, or a Log Ninja import: only a
  // minutes total is known. It stays as it is until times are added.
  const legacyMinutes = !isNew && original.length === 0 && (shift.break_minutes || 0) > 0;

  const setBreak = (key, patch) => set({ breaks: draft.breaks.map((b) => (b.key === key ? { ...b, ...patch } : b)) });
  const addBreak = () => set({ breaks: [...draft.breaks, breakRow()] });
  const removeBreak = (key) => set({ breaks: draft.breaks.filter((b) => b.key !== key) });

  const submit = () => onSubmit(
    { start: draft.start, finish: draft.finish, day_type: draft.day_type, note: draft.note },
    breakBody(draft.breaks),
    breaksChanged,
  );

  return (
    <div className={`rounded-xl border p-3 space-y-3 ${isNew ? "border-dashed border-slate-700 bg-slate-900/20" : "border-slate-800/80 bg-slate-900/40"}`}>
      {!isNew && (
        <div className="flex flex-wrap items-center gap-2 text-[10px]">
          <span className={`px-2 py-0.5 rounded font-bold uppercase tracking-wide ${shift.source === "manual" ? "bg-violet-500/10 text-violet-300 border border-violet-500/30" : "bg-slate-800 text-slate-400 border border-slate-700"}`}>
            {shift.source === "manual" ? `keyed in by ${shift.entered_by || "the office"}` : shift.source}
          </span>
          <span className="text-slate-500 font-mono">{fmtHours(shift.paid_hours)} h paid</span>
          {(shift.break_minutes || 0) > 0 && (
            <span className="text-slate-500 font-mono">{shift.break_minutes} min break, paid</span>
          )}
          {shift.on_break && (
            <span className="text-emerald-300 font-semibold">on a break now</span>
          )}
          {shift.locked && (
            <span className="flex items-center gap-1 text-amber-400"><Lock size={10} /> locked by an approved fortnight</span>
          )}
        </div>
      )}

      {!isNew && (shift.flags || []).length > 0 && (
        <div className="space-y-1">
          {shift.flags.map((f, i) => (
            <div key={`${f.code}-${i}`} className={`text-[11px] rounded-lg border px-2 py-1.5 ${FLAG_TINT[f.tier] || FLAG_TINT.info}`}>
              <span className="font-bold mr-1.5">{f.code}</span>{f.message}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 text-xs">
        <label className="space-y-1">
          <span className={LABEL}>Start</span>
          <input type="time" className={TIME_INPUT} value={draft.start} onChange={(e) => set({ start: e.target.value })} />
        </label>
        <label className="space-y-1">
          <span className={LABEL}>Finish</span>
          <input type="time" className={TIME_INPUT} value={draft.finish} onChange={(e) => set({ finish: e.target.value })} />
        </label>
        <label className="space-y-1">
          <span className={LABEL}>Day type</span>
          <select className={`${INPUT} cursor-pointer`} value={draft.day_type} onChange={(e) => set({ day_type: e.target.value })}>
            {DAY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>
      </div>

      {/* Breaks are recorded for work-time visibility only - they are paid
          and are never taken off the hours. Each one is a start and a finish,
          because the 30-minute rest rule is about one continuous break and a
          total of minutes cannot show that. */}
      <div className="space-y-1.5 text-xs">
        <div className="flex items-center justify-between gap-2">
          <span className={LABEL}>Breaks (paid, never deducted)</span>
          <button type="button" onClick={addBreak} disabled={busy}
            className="flex items-center gap-1 text-[11px] font-semibold text-indigo-300 hover:text-indigo-200 disabled:opacity-50">
            <Plus size={12} /> Add break
          </button>
        </div>

        {legacyMinutes && (
          <p className="text-[11px] text-slate-400 bg-slate-950/60 border border-slate-800 rounded-lg px-2.5 py-1.5">
            {shift.break_minutes} min recorded, no times
            {draft.breaks.length > 0 && <span className="text-slate-500"> - the times below replace this total when saved</span>}
          </p>
        )}
        {draft.breaks.length === 0 && !legacyMinutes && (
          <p className="text-[11px] text-slate-500">No break recorded.</p>
        )}

        {draft.breaks.map((b, i) => (
          <div key={b.key} className="flex items-center gap-2">
            <span className="w-4 text-right text-[10px] font-mono text-slate-500">{i + 1}</span>
            <input type="time" aria-label="Break start" className={TIME_INPUT} value={b.start}
              onChange={(e) => setBreak(b.key, { start: e.target.value })} />
            <span className="text-slate-500">to</span>
            <input type="time" aria-label="Break finish" className={TIME_INPUT} value={b.finish}
              onChange={(e) => setBreak(b.key, { finish: e.target.value })} />
            <button type="button" onClick={() => removeBreak(b.key)} disabled={busy} title="Remove this break"
              className="p-1.5 rounded-lg text-slate-500 hover:text-red-300 hover:bg-red-500/10 disabled:opacity-50">
              <X size={13} />
            </button>
          </div>
        ))}

        {incomplete && (
          <p className="text-[10px] text-amber-400">Every break needs a start and a finish before this day can be saved.</p>
        )}
      </div>

      <input className={`${INPUT} text-xs`} placeholder="Note (why this day was changed, if it was)"
        value={draft.note} onChange={(e) => set({ note: e.target.value })} />

      <div className="flex items-center justify-between gap-2">
        <button
          onClick={submit}
          disabled={busy || !draft.start || incomplete}
          className={`px-4 py-2 rounded-lg text-xs font-bold text-white transition-all disabled:opacity-50 ${isNew ? "bg-indigo-600 hover:bg-indigo-500" : "bg-blue-600 hover:bg-blue-500"}`}
        >
          {isNew ? <span className="flex items-center gap-1.5"><Plus size={13} /> Add this shift</span> : "Save changes"}
        </button>
        {!isNew && (
          <button onClick={() => onDelete(shift)} disabled={busy}
            className="text-red-400 hover:text-red-300 p-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/25 rounded-lg transition-all disabled:opacity-50"
            title="Delete this shift">
            <Trash2 size={13} />
          </button>
        )}
      </div>
      {!draft.start && <p className="text-[10px] text-slate-500">A start time is required. Leave the finish blank for a shift that is still running.</p>}
    </div>
  );
}

export default function DayEditor({ person, day, shifts, periodStatus, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(shifts.length === 0);
  // Break rows the server refused, by shift id. The form below is re-keyed
  // on edited_at after every save, so without this a refused list would be
  // replaced by the server's rows and the office would type it all again.
  const [held, setHeld] = useState({});

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChanged();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  // A new day carries its breaks in the same POST; the server creates the
  // rows and derives break_minutes from them.
  const create = (fields, breaks) => run(async () => {
    await API.post("/time/shifts", {
      person_id: person.id, work_date: day, ...fields,
      ...(breaks.length ? { breaks } : {}),
    });
    setAdding(false);
  });

  // PATCH the day first, then replace its breaks - and only when the rows
  // were touched, so a save that only fixed the finish time writes no
  // "breaks" audit row and never disturbs a legacy minutes total.
  //
  // A PUT the server refuses (overlap, a break outside the shift, a locked
  // fortnight) does not undo the PATCH that went before it: the grid and
  // Roster behind the modal are still refreshed so they show the saved
  // fields, the server's reason is shown, and the typed rows are held so
  // the re-keyed form comes back with them rather than the server's.
  const save = async (shift, fields, breaks, breaksChanged) => {
    setBusy(true);
    setError(null);
    try {
      await API.patch(`/time/shifts/${shift.id}`, fields);
      let refused = null;
      if (breaksChanged) {
        try {
          await API.put(`/time/shifts/${shift.id}/breaks`, { breaks });
        } catch (err) {
          refused = errMsg(err);
        }
      }
      setHeld((h) => {
        const next = { ...h };
        if (refused) next[shift.id] = breaks; else delete next[shift.id];
        return next;
      });
      if (refused) setError(`The day's times were saved, but its breaks were refused: ${refused}`);
      await onChanged();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = (shift) => {
    if (!window.confirm(`Delete ${person.name}'s ${shift.start || "shift"} on ${day}? The audit trail keeps a record of the deletion.`)) return;
    run(() => API.delete(`/time/shifts/${shift.id}`));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto py-10" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-2xl p-5 w-full max-w-2xl shadow-2xl space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-white">{person.name}</h3>
            <p className="text-xs text-slate-400">
              {prettyDate(day)}
              <span className={`ml-2 px-2 py-0.5 rounded text-[10px] font-bold ${person.role === "driver" ? "bg-blue-950/50 text-blue-400 border border-blue-500/20" : "bg-teal-950/50 text-teal-400 border border-teal-500/20"}`}>
                {person.role}
              </span>
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>

        {periodStatus === "approved" && (
          <div className="flex items-start gap-2 text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>This fortnight is approved, so drivers can no longer touch it. An admin change here still goes through, and every field change is written to the audit trail.</span>
          </div>
        )}

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 text-xs text-red-300 flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span className="flex-1 break-words">{error}</span>
          </div>
        )}

        {shifts.map((s) => (
          // Re-key on the edit stamp so a saved shift comes back with the
          // server's own values rather than the draft that was typed.
          <ShiftForm key={`${s.id}-${s.edited_at || ""}`} shift={s} heldBreaks={held[s.id]} busy={busy}
            onSubmit={(fields, breaks, changed) => save(s, fields, breaks, changed)} onDelete={remove} />
        ))}

        {adding ? (
          <ShiftForm shift={null} busy={busy} onSubmit={create} onDelete={() => {}} />
        ) : (
          <button onClick={() => setAdding(true)}
            className="w-full text-xs text-slate-400 hover:text-white border border-dashed border-slate-700 hover:border-slate-500 rounded-xl py-2.5 flex items-center justify-center gap-1.5 transition-all">
            <Plus size={13} /> Add another shift on this day
          </button>
        )}

        {shifts.length === 0 && !adding && (
          <p className="text-xs text-slate-500 text-center">Nothing recorded for this day.</p>
        )}
      </div>
    </div>
  );
}
